import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import * as yup from "yup";
import AppInput from "../../components/Core/AppInput/AppInput";
import AppText from "../../components/Core/AppText/AppText";
import Button from "../../components/Core/Button/Button";
import Flex from "../../components/Layout/Flex";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import FloatingButtonPanel from "../../components/Shared/FloatingButtonPanel/FloatingButtonPanel";
import { CreateRecruitmentBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { routes } from "../../configs/constants/routes";
import { CreateCandidateRequest } from "../../models/Candidate";
import { useCreateCandidate } from "../../networking/candidate.hooks";
import phoneNumberSchema from "../../schemas/phoneNumberSchema";
import postalCodeSchema from "../../schemas/postalCodeSchema";
import socialSecurityNumberSchema from "../../schemas/socialSecurityNumberSchema";
import { useOrganisation } from "../../networking/organisation.hooks";
import { useSubsidiaries } from "../../networking/subsidiary.hooks";
import { DropdownController } from "../../components/Core/Dropdown/Dropdown";
import { Pool } from "../../models/Organisation";
import SubsidiarySelect, {
	useAvailablePools,
} from "../../components/Shared/SubsidiarySelect/SubsidiarySelect";

const CreateCandidateInputsValidationSchema = yup.object().shape({
	email: yup.string().email("EMAIL_NOT_VALID").required("FIELD_IS_REQUIRED"),
	firstName: yup.string().required("FIELD_IS_REQUIRED"),
	lastName: yup.string().required("FIELD_IS_REQUIRED"),
	profile: yup.object().shape({
		phoneNumber: phoneNumberSchema,
		socialSecurityNumber: socialSecurityNumberSchema,
		address: yup.object().shape({
			postalCode: postalCodeSchema,
		}),
	}),
});

const RecruitmentCreatePage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { data: organisation } = useOrganisation();
	const { subsidiaries } = useSubsidiaries();

	const { mutate: createCandidate } = useCreateCandidate();

	const { register, handleSubmit, formState, setValue, control } = useForm<CreateCandidateRequest>({
		mode: "onBlur",
		resolver: yupResolver(CreateCandidateInputsValidationSchema),
	});

	const availablePools = useAvailablePools(
		control,
		"profile.subsidiaryId",
		subsidiaries,
		organisation?.pools
	);

	const onSubmit = handleSubmit(async (e) => {
		const createCandidateRequest: CreateCandidateRequest = {
			firstName: e.firstName,
			lastName: e.lastName,
			email: e.email,
			profile: {
				socialSecurityNumber: e.profile.socialSecurityNumber,
				phoneNumber: e.profile.phoneNumber,
				address: e.profile.address,
				pool: e.profile.pool || undefined,
				subsidiaryId: e.profile.subsidiaryId || undefined,
			},
		};

		await createCandidate(createCandidateRequest);
		navigate(routes.RECRUITMENT);
	});

	useEffect(() => {
		if (organisation) {
			const defaultPool = organisation?.pools?.find((p) => p.isDefault);

			if (defaultPool) {
				setValue("profile.pool", defaultPool.key);
			}
		}
	}, [organisation]);

	return (
		<div className="p-4 px-6">
			<Breadcrumbs items={CreateRecruitmentBreadcrumbs} />

			<form onSubmit={onSubmit}>
				<AppText margin="1.5rem 0" color="grayText" size="xm" weight="bold">
					{t("BASIC_DETAILS")}
				</AppText>
				<Flex width="50%">
					<AppInput
						{...register("firstName")}
						icon="name"
						label="FIRST_NAME"
						placeholder="FIRST_NAME"
						errors={formState.errors?.firstName?.message}
						margin="0 1rem 0 0"
					/>
					<AppInput
						{...register("lastName")}
						icon="name"
						label="LAST_NAME"
						placeholder="LAST_NAME"
						errors={formState.errors?.lastName?.message}
					/>
				</Flex>

				<Flex width="50%">
					<AppInput
						{...register("email")}
						icon="email"
						label="EMAIL"
						placeholder="EMAIL"
						errors={formState.errors?.email?.message}
						margin="0 1rem 0 0"
					/>
					<AppInput
						{...register("profile.phoneNumber")}
						icon="phone"
						label="PHONE"
						placeholder={t("PHONE_PLACEHOLDER")}
						errors={formState.errors?.profile?.phoneNumber?.message}
					/>
				</Flex>

				<Flex width="25%">
					<AppInput
						{...register("profile.socialSecurityNumber")}
						label="PERSONAL_NUMBER"
						placeholder="ÅÅÅÅMMDD-XXXX"
						errors={formState.errors?.profile?.socialSecurityNumber?.message}
						margin="0 1rem 0 0"
					/>
				</Flex>

				{subsidiaries.length > 0 && (
					<Flex width="25%">
						<SubsidiarySelect
							control={control}
							name="profile.subsidiaryId"
							subsidiaries={subsidiaries}
						/>
					</Flex>
				)}

				{availablePools.length > 0 && (
					<Flex width="25%">
						<DropdownController<Pool>
							control={control}
							label="POOL"
							name="profile.pool"
							options={availablePools}
							getOptionLabel={(option) => option.name}
							getValue={(o) => o.key as string}
							placeholder="SELECT_POOL"
						/>
					</Flex>
				)}

				<Flex>
					<Flex margin="0.5rem 0" width="25%" direction="column">
						<AppInput
							{...register("profile.address.address1")}
							label="ADDRESS"
							placeholder={t("ADDRESS_PLACEHOLDER")}
							containerMargin="0"
							margin="0 1rem 0 0"
						/>
						<Flex width="100%">
							<AppInput
								{...register("profile.address.postalCode")}
								containerWidth="60%"
								placeholder="POSTAL_CODE_PLACEHOLDER"
								errors={formState.errors?.profile?.address?.postalCode?.message}
								margin="0 0.5rem 0 0"
							/>
							<AppInput
								{...register("profile.address.city")}
								width="fit-content"
								placeholder="CITY_PLACEHOLDER"
								margin="0 1rem 0 0"
							/>
						</Flex>
					</Flex>
				</Flex>
				<FloatingButtonPanel>
					<Button disabled={formState.isSubmitting}>{t("CREATE_CANDIDATE")}</Button>
				</FloatingButtonPanel>
			</form>
		</div>
	);
};

export default RecruitmentCreatePage;
